import { preProjectViewsService } from '@/services/pre-project-views.service';
import { i18n } from '@/plugins/i18n';

// Built-in "All" tab (no filters), shown ahead of the API's views
export const BUILT_IN_VIEW = {
  ALL: 'all',
};

export const VIEW_SCOPE = {
  PRIVATE: 'PRIVATE',
  SHARED: 'SHARED',
};

// Hard cap enforced by the DB on a view name (same as tender views).
export const VIEW_NAME_MAX_LENGTH = 120;

const BUILT_IN_VIEWS = [{ id: BUILT_IN_VIEW.ALL, nameKey: 'page.pre-projects.views.all', builtIn: true, filters: {} }];

// Persist only meaningful filter values on a view (mirrors the search body:
// null values and empty arrays are omitted).
const sanitizeFilters = (filters) => {
  const clean = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value) && value.length === 0) return;
    clean[key] = value;
  });
  return clean;
};

// Stable representation for dirty comparison: sanitized + key-sorted, so an
// unset null and a missing key (or key order) never read as a difference.
const normalizeForCompare = (filters) => {
  const clean = sanitizeFilters(filters);
  const sorted = {};
  Object.keys(clean)
    .sort()
    .forEach((key) => {
      sorted[key] = clean[key];
    });
  return JSON.stringify(sorted);
};

const initialState = () => ({
  // Views as returned by the API, pre-sorted.
  views: [],
  activeViewId: null,
  // The active view's saved filters (working-set shape) for dirty comparison.
  baselineFilters: {},
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

// Create a view, then make it the active one.
function createAndSelectView({ commit, dispatch }, { name, scope, filters }) {
  return preProjectViewsService.create({ name, scope, filters }).then((created) => {
    commit('UPSERT_VIEW', created);
    return dispatch('selectView', created.id).then(() => created);
  });
}

const actions = {
  loadViews({ commit }) {
    commit('LOAD_VIEWS');
    return preProjectViewsService.list().then(
      (views) => {
        commit('LOAD_VIEWS_SUCCESS', views);
        return views;
      },
      (error) => {
        commit('ERROR', error);
      },
    );
  },

  // Switch the active view and push its saved filters into the pre-projects
  // search filters (full replace: clear first so unset keys fall back to defaults).
  selectView({ commit, getters, dispatch }, viewId) {
    const view = getters.viewById(viewId);
    if (!view) return Promise.resolve();
    commit('SET_ACTIVE_VIEW', viewId);
    commit('SET_BASELINE_FILTERS', view.filters || {});
    return dispatch('preProjects/clearFilters', null, { root: true }).then(() =>
      dispatch('preProjects/setFilters', view.filters || {}, { root: true }),
    );
  },

  // Persist the current working filters onto the active (editable) view.
  updateActiveView({ commit, getters, rootGetters }) {
    const view = getters.activeView;
    if (!view || !getters.canEditActiveView) return Promise.resolve();
    const filters = sanitizeFilters(rootGetters['preProjects/filters']);
    return preProjectViewsService.update(view.id, { filters }).then((updated) => {
      commit('UPSERT_VIEW', updated);
      commit('SET_BASELINE_FILTERS', updated.filters || {});
      return updated;
    });
  },

  // Discard working edits, restoring the active view's saved filters.
  resetActiveView({ getters, dispatch }) {
    const view = getters.activeView;
    if (!view) return Promise.resolve();
    return dispatch('preProjects/clearFilters', null, { root: true }).then(() =>
      dispatch('preProjects/setFilters', view.filters || {}, { root: true }),
    );
  },

  // "New view": sends no filters so the backend stores its defaults (unfiltered, like the built-in
  // "All" tab) — never the active view's filters (that's saveAsNewView).
  createView(context, { name, scope = VIEW_SCOPE.PRIVATE } = {}) {
    return createAndSelectView(context, { name, scope });
  },

  // Save the current working filters as a new view, then make it active.
  saveAsNewView(context, { name, scope = VIEW_SCOPE.PRIVATE } = {}) {
    const filters = sanitizeFilters(context.rootGetters['preProjects/filters']);
    return createAndSelectView(context, { name, scope, filters });
  },

  renameView({ commit, getters }, { viewId, name }) {
    const view = getters.viewById(viewId);
    if (!view || view.builtIn) return Promise.resolve();
    return preProjectViewsService.update(viewId, { name }).then((updated) => commit('UPSERT_VIEW', updated));
  },

  // Duplicate a view from its SAVED filters as a new private view.
  duplicateView(context, viewId) {
    const view = context.getters.viewById(viewId);
    if (!view) return Promise.resolve();
    // Append the copy suffix, truncating the base name so the total stays within the DB cap.
    const suffix = ` (${i18n.t('page.tenders.views.duplicate-suffix')})`;
    const baseName = (view.nameKey ? i18n.t(view.nameKey) : view.name)
      .slice(0, Math.max(0, VIEW_NAME_MAX_LENGTH - suffix.length))
      .trimEnd();
    return createAndSelectView(context, {
      name: `${baseName}${suffix}`,
      scope: VIEW_SCOPE.PRIVATE,
      filters: view.filters || {},
    });
  },

  toggleScope({ commit, getters }, viewId) {
    const view = getters.viewById(viewId);
    if (!view || view.builtIn) return Promise.resolve();
    const scope = view.scope === VIEW_SCOPE.SHARED ? VIEW_SCOPE.PRIVATE : VIEW_SCOPE.SHARED;
    return preProjectViewsService.update(viewId, { scope }).then((updated) => commit('UPSERT_VIEW', updated));
  },

  // Persist this user's order of the API views (the built-in "All" tab is
  // client-side, always first, and never part of the ordered list). The local
  // order updates optimistically; the PUT persists it.
  reorder({ commit }, orderedIds) {
    commit('SET_VIEWS_ORDER', orderedIds);
    return preProjectViewsService.reorder(orderedIds);
  },

  removeView({ commit, getters, dispatch, state }, viewId) {
    const view = getters.viewById(viewId);
    if (!view || view.builtIn) return Promise.resolve();
    return preProjectViewsService.remove(viewId).then(() => {
      commit('REMOVE_VIEW', viewId);
      // Deleting the active view falls back to the built-in "All" tab.
      if (state.activeViewId === viewId) return dispatch('selectView', BUILT_IN_VIEW.ALL);
    });
  },
};

const mutations = {
  LOAD_VIEWS(state) {
    state.status = { error: null, pending: true, resolved: false, rejected: false };
  },
  LOAD_VIEWS_SUCCESS(state, views) {
    state.status = { error: null, pending: false, resolved: true, rejected: false };
    state.views = views || [];
  },
  ERROR(state, error) {
    state.status = { ...state.status, pending: false, rejected: true, error };
  },
  SET_ACTIVE_VIEW(state, viewId) {
    state.activeViewId = viewId;
  },
  SET_BASELINE_FILTERS(state, filters) {
    state.baselineFilters = filters;
  },
  UPSERT_VIEW(state, view) {
    const index = state.views.findIndex((existing) => existing.id === view.id);
    if (index === -1) state.views.push(view);
    else state.views.splice(index, 1, view);
  },
  REMOVE_VIEW(state, viewId) {
    state.views = state.views.filter((view) => view.id !== viewId);
  },
  SET_VIEWS_ORDER(state, orderedIds) {
    const byId = new Map(state.views.map((view) => [view.id, view]));
    const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean);
    // Keep any views not present in orderedIds at the end.
    state.views.forEach((view) => {
      if (!orderedIds.includes(view.id)) reordered.push(view);
    });
    state.views = reordered;
  },
  RESET(state) {
    Object.assign(state, initialState());
  },
};

const getters = {
  // Built-in tabs first, then the saved views from the API.
  views: (state) => [...BUILT_IN_VIEWS, ...state.views],
  allViews: (state, getters) => getters.views,
  viewById: (state, getters) => (id) => getters.views.find((view) => view.id === id) || null,
  activeViewId: (state) => state.activeViewId,
  activeView: (state, getters) => getters.viewById(state.activeViewId),
  // Server is source of truth for edit rights (owner or tenant admin); the DTO
  // carries `canEdit`. Built-ins and presets are never editable client-side.
  canEditView: () => (view) => !!(view && !view.builtIn && view.canEdit),
  canEditActiveView: (state, getters) => getters.canEditView(getters.activeView),
  // Dirty = working filters differ from the active view's saved baseline.
  isActiveViewDirty: (state, getters, rootState, rootGetters) => {
    if (!getters.activeView) return false;
    return normalizeForCompare(rootGetters['preProjects/filters']) !== normalizeForCompare(state.baselineFilters);
  },
  pending: (state) => state.status.pending,
  error: (state) => state.status.error,
};

export const preProjectViews = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
